import { useCallback, useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * @typedef {{tab: string, path: string}} Tab
 * */

/**
 * Custom hook to handle Chakra Tabs based on location
 * @param {Tab[]} tabs - Array of tab objects
 * @example [{tab: 'Login',path: '/'}, {tab: 'Register',path: '/register'}]
 * @returns {{tabIndex: number, handleTabsChange: (index: number) => void}} - { tabIndex, handleTabsChange }
 * */
function useTabRoute(tabs) {
    const homePath = '/';
    const location = useLocation();
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(0);

    const memoizedTabs = useMemo(() => tabs || [], [tabs]);

    const findTabIndex = useCallback((pathname, tabsArray) => {
        if (!pathname || !tabsArray?.length) return 0; // default index

        const exactMatch = tabsArray?.findIndex((tab) => tab.path === pathname);
        if (exactMatch !== -1) return exactMatch;

        // Sort by path length (descending) to match more specific paths first
        const sortedTabs = [...tabsArray]
            .map((t, i) => ({
                ...t,
                originalIndex: i,
            }))
            .sort((a, b) => b?.path.length - a?.path.length);
        for (const tab of sortedTabs) {
            if (tab.path !== homePath && pathname.includes(tab.path)) {
                return tab?.originalIndex;
            }
        }

        const rootTab = tabsArray.findIndex((t) => t.path === homePath);
        if (pathname === homePath && rootTab !== -1) {
            return rootTab;
        }

        return 0;
    }, []);

    // Side effect to tab index when location changes
    useEffect(() => {
        const newIndex = findTabIndex(location.pathname, memoizedTabs);
        setTabIndex((prevIndex) => {
            // Only update if index actually changed to prevent unnecessary re-renders
            return prevIndex !== newIndex ? newIndex : prevIndex;
        });
    }, [location.pathname, memoizedTabs, findTabIndex]);

    const handleTabsChange = useCallback(
        (index) => {
            if (index < 0 || index >= memoizedTabs.length) {
                return;
            }

            const targetTab = memoizedTabs[index];
            if (!targetTab?.path) {
                return;
            }

            // Only navigate if we're not already on the target path
            if (location.pathname !== targetTab.path) {
                setTabIndex(index);
                navigate(targetTab.path);
            }
        },
        [memoizedTabs, navigate, location.pathname],
    );

    return {
        tabIndex,
        handleTabsChange,
    };
}

export { useTabRoute };
